import { useRef, useState } from 'react';
import { moveNote, resizeNote } from '../util/notes.mjs';

const movementThreshold = 5;

const arrowDirections = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1]
};

const adjustGeometry = function(note, type, dx, dy, board, headerHeight) {
  const snap = board.dataset.snap === 'true';
  const bottom = (board.clientHeight - (Number(board.dataset.panY) || 0)) / (Number(board.dataset.zoom) || 1);

  if (type === 'move' && snap) {
    dx = Math.round((note.x + dx) / 20) * 20 - note.x;
    dy = Math.round((note.y + dy) / 20) * 20 - note.y;
  }

  return type === 'move' ? moveNote(note, dx, dy, bottom, headerHeight) : resizeNote(note, dx, dy, snap);
};

// Both handles share pointer capture, cancellation, and keyboard controls.
const useNoteGesture = function(note, onChange) {
  const dragRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);

  const startGesture = function(event, type) {
    if (!event.isPrimary || event.button !== 0 || dragRef.current) return;

    const handle = event.currentTarget;
    const board = handle.closest('.board');

    // Keep the starting geometry fixed: each delta is measured from pointer-down.
    dragRef.current = {
      type,
      zoom: Number(board.dataset.zoom) || 1,
      headerHeight: handle.closest('.note').querySelector('.noteHead')?.offsetHeight ?? 28,
      note,
      board,
      handle,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      panX: Number(board.dataset.panX) || 0,
      panY: Number(board.dataset.panY) || 0,
      moved: false
    };
    handle.setPointerCapture(event.pointerId);
    // Focusing a large or partly hidden image must not scroll the board.
    handle.focus({preventScroll: true});
    event.preventDefault();
  };

  const moveGesture = function(event) {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) return;

    // Account for panning so the note stays under the pointer.
    const dx = (event.clientX - drag.x - (Number(drag.board.dataset.panX) || 0) + drag.panX) / drag.zoom;
    const dy = (event.clientY - drag.y - (Number(drag.board.dataset.panY) || 0) + drag.panY) / drag.zoom;

    // Ordinary clicks should select the note without moving it.
    if (!drag.moved && Math.hypot(dx, dy) < movementThreshold) return;

    if (!drag.moved) {
      drag.moved = true;
      setIsDragging(true);
      window.getSelection()?.removeAllRanges();
    }

    onChange(note.id, adjustGeometry(drag.note, drag.type, dx, dy, drag.board, drag.headerHeight));
    event.preventDefault();
  };

  const finishGesture = function(event) {
    const drag = dragRef.current;

    if (!drag || drag.pointerId !== event.pointerId) return;

    // Clear first: releasing capture can also trigger lostpointercapture.
    dragRef.current = null;
    setIsDragging(false);

    if (event.type === 'pointercancel') {
      const {x, y, width, height} = drag.note;

      onChange(note.id, {
        x,
        y,
        width,
        height
      });
    }

    if (drag.handle.hasPointerCapture(event.pointerId)) {
      drag.handle.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = function(event, type) {
    if (event.key === 'Escape' && dragRef.current) {
      finishGesture({
        pointerId: dragRef.current.pointerId,
        type: 'pointercancel'
      });

      return;
    }

    const direction = arrowDirections[event.key];

    if (!direction) return;

    const handle = event.currentTarget;
    const board = handle.closest('.board');
    const headerHeight = handle.closest('.note').querySelector('.noteHead')?.offsetHeight ?? 28;
    const step = event.shiftKey ? 40 : board.dataset.snap === 'true' ? 20 : 10;

    const [dx, dy] = direction.map(offset => offset * step);

    event.preventDefault();
    onChange(note.id, adjustGeometry(note, type, dx, dy, board, headerHeight));
  };

  const getHandleProps = function(type) {
    return {
      onPointerDown: event => startGesture(event, type),
      onPointerMove: moveGesture,
      onPointerUp: finishGesture,
      onPointerCancel: finishGesture,
      onLostPointerCapture: finishGesture,
      onKeyDown: event => handleKeyDown(event, type)
    };
  };

  return {
    isDragging,
    getHandleProps
  };
};

export default useNoteGesture;
